// Copyright 2022, Imprivata, Inc.  All rights reserved.
import {
 Button, TypeIcon } from '@imprivata-cloud/components';
import { Alert, Flex, Typography } from 'antd';
import DemographicsCard from '../../components/demographics/DemographicsCard';
import LoadingSpinner from '../../components/loading/Loading';
import Photo from '../../components/photo/Photo';
import './EnrollPage.less';
import { useMemo } from 'react';
import { ErrorComponent } from '../../components/error/ErrorComponent';
import { useEnrollPageState } from './states/useEnrollPageState';
import { navigateWithContext } from '@utils/NavigationHelper';
import { useTranslation } from 'react-i18next';
const EnrollPage = () => {
  const { state, actions } = useEnrollPageState();
  const { t } = useTranslation();
  // Memoize loading state for clarity
  const isLoading = useMemo(
    () =>
      (!state.error?.message &&
        (state.isEnrollmentPending ||
          state.isInquiryLoading ||
          state.isGrantStatusLoading ||
          state.isPatientLoading)) ||
      // Also show loading if we have errors but are still waiting for grant status
      ((state.inquiryError || state.isInquiryLoading || state.createEnrollmentError) && state.isGrantStatusLoading),
    [
      state.error?.message,
      state.isEnrollmentPending,
      state.isInquiryLoading,
      state.isGrantStatusLoading,
      state.isPatientLoading,
      state.inquiryError,
      state.createEnrollmentError,
    ],
  );
  if (state.continueOnOriginalDevice) {
    return <ErrorComponent cause="inquiry-session-mismatch" />;
  }
  if (state.showPatientAlreadyEnrolledError) {
    return <ErrorComponent cause="patient-enrolled" />;
  }

  if (isLoading) {
    return (
      <>
        <Typography.Title
          level={2}
          className="text-center"
          data-testid="enroll-loading-title"
        >
          {t('enrollPage.were-processing-your-information')}
        </Typography.Title>
        <LoadingSpinner />
      </>
    );
  }

  if (state.error?.message === "Invalid request" && state.inquiryError) {
    return <ErrorComponent cause="link-expired" />;
  }

  if (state.grantStatus?.pendingHandoff && !state.error?.message) {
    return (
      <>
        <Alert
          message={t('enrollPage.identity-verification-completed')}
          type="info"
          className="w-100 impr-alert impr-alert-info"
          icon={<TypeIcon type="info" />}
          showIcon={true}
          data-testid="enroll-handoff-alert"
        />
        <Typography.Paragraph className="text-center mt-16" data-testid="enroll-handoff-instructions">
          {t('enrollPage.you-can-close-this-window-and-return-to-your-original-device')}
        </Typography.Paragraph>
      </>
    );
  }
  const getPrimaryButtonLabel = () =>
    state.grantStatus?.pendingAuthorization ? t('enrollPage.continue') : undefined;
  const handlePrimaryClick = () => {
    if (state.enrollmentSuccess) {
      navigateWithContext(actions.navigate, '/consent');
    } else {
      actions.postGrant();
    }
  };
  return (
    <Flex align="center" gap={15} vertical className='mt-1 w-100'>
      {state.showPhoto && <Photo image={state.photo ?? undefined} />}
      {!state.error?.message && (
        <DemographicsCard
          givenName={state.patient?.givenName}
          surname={state.patient?.surname}
          dateOfBirth={state.patient?.dateOfBirth}
          mrn={state.patient?.mrn}
          isLoading={state.isPatientLoading}
        />
      )}
      {state.error?.message && (
        <Flex align="center" gap={16} vertical>
          <Alert
            message={state.error?.message}
            type={state.error.type || 'error'}
            className={`w-100 impr-alert impr-alert${
              (state.error.type || 'error') === 'warning'
                ? ' impr-alert-warning'
                : ' impr-alert-error'
            }`}
            icon={<TypeIcon type={state.error.type || 'error'} />}
            showIcon={true}
          />
          <Typography.Text className="text-center" style={{ whiteSpace: 'pre-line' }}>
            {state.error?.instructions}
          </Typography.Text>
        </Flex>
      )}
      {state.enrollmentSuccess && (
        <>
          <Alert
            message={t('enrollPage.successful-enrollment')}
            type="success"
            className="w-100 impr-alert impr-alert-success"
            icon={<TypeIcon type="success" />}
            showIcon={true}
            data-testid="enroll-success-alert"
          />
          {!state.grantStatus?.pendingAuthorization && (
            <Typography.Text className="text-center" data-testid="enroll-close-message">{t('enrollPage.you-may-close-this-window')}</Typography.Text>
          )}
        </>
      )}
      {state.grantStatus?.pendingAuthorization && !state.grantStatus?.pendingHandoff && (
        <Button
          type="primary"
          label={getPrimaryButtonLabel()}
          data-testid="enroll-continue-btn"
          onClick={handlePrimaryClick}
          className="w-100 max-width-button"
          autoFocus
        />
      )}
    </Flex>
  );
};
export default EnrollPage;
